import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { KeyRound, Search, ShieldCheck, UserRound } from "lucide-react";
import AdminGate from "@/components/admin/AdminGate";
import { SectionHeading } from "@/components/SectionHeading";
import { getStudentAccounts } from "@/lib/api";

const Container = ({ children }) => (
  <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">{children}</div>
);

const fmtDate = (value) => {
  if (!value) return "Never";
  try {
    return new Date(value).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (error) {
    return value;
  }
};

function StudentManager() {
  const [query, setQuery] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-student-accounts"],
    queryFn: getStudentAccounts,
  });
  const rows = useMemo(() => data || [], [data]);
  const filtered = useMemo(() => {
    const value = query.trim().toLowerCase();
    if (!value) return rows;
    return rows.filter((row) =>
      [row.email, row.first_name, row.shopify_order_id, row.access_level]
        .some((field) => String(field || "").toLowerCase().includes(value))
    );
  }, [query, rows]);
  const full = rows.filter((row) => row.has_full_access).length;
  const free = rows.length - full;

  return (
    <div>
      <div className="grid gap-3 sm:grid-cols-3">
        {[
          ["Total accounts", rows.length],
          ["Full access", full],
          ["Free accounts", free],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg border border-[color:rgba(10,27,51,0.12)] bg-white px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">{label}</p>
            <p className="mt-1 text-2xl font-bold text-[var(--ibp-ink)]">{isLoading ? "-" : value}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <label className="relative block w-full sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:rgba(11,18,32,0.45)]" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search name, email, or order"
            className="h-10 w-full rounded-lg border border-[color:rgba(10,27,51,0.18)] bg-white pl-9 pr-3 text-sm outline-none focus:border-[var(--ibp-navy-950)]"
          />
        </label>
        <Link to="/admin/access-codes" className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-[color:rgba(10,27,51,0.18)] bg-white px-4 py-2 text-sm font-semibold text-[var(--ibp-ink)]">
          <KeyRound className="h-4 w-4" /> Legacy codes
        </Link>
      </div>

      <div className="mt-5 overflow-x-auto rounded-lg border border-[color:rgba(10,27,51,0.12)] bg-white">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-[var(--ibp-offwhite)] text-xs uppercase tracking-wide text-[color:rgba(11,18,32,0.55)]">
            <tr>
              <th className="px-4 py-3">Student</th>
              <th className="px-4 py-3">Access</th>
              <th className="px-4 py-3">Progress</th>
              <th className="px-4 py-3">Purchase</th>
              <th className="px-4 py-3">Last sign-in</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:rgba(10,27,51,0.08)]">
            {filtered.map((row) => (
              <tr key={row.account_id}>
                <td className="px-4 py-4 align-top">
                  <p className="font-semibold text-[var(--ibp-ink)]">{row.first_name || "No first name"}</p>
                  <p className="mt-0.5 text-xs text-[color:rgba(11,18,32,0.6)]">{row.email}</p>
                </td>
                <td className="px-4 py-4 align-top">
                  <span className={[
                    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
                    row.has_full_access
                      ? "bg-[color:rgba(21,128,61,0.12)] text-[#15803d]"
                      : "bg-[color:rgba(10,27,51,0.08)] text-[color:rgba(11,18,32,0.68)]",
                  ].join(" ")}>
                    {row.has_full_access ? <ShieldCheck className="h-3.5 w-3.5" /> : <UserRound className="h-3.5 w-3.5" />}
                    {row.has_full_access ? "Full" : "Free"}
                  </span>
                </td>
                <td className="px-4 py-4 align-top text-[color:rgba(11,18,32,0.68)]">
                  <p>{row.attempted_count} questions</p>
                  <p className="mt-0.5 text-xs">{row.completed_lessons} lessons</p>
                </td>
                <td className="px-4 py-4 align-top text-[color:rgba(11,18,32,0.68)]">
                  <p>{row.purchase_source || "-"}</p>
                  <p className="mt-0.5 max-w-40 truncate text-xs" title={row.shopify_order_id}>{row.shopify_order_id || "No paid order"}</p>
                </td>
                <td className="whitespace-nowrap px-4 py-4 align-top text-xs text-[color:rgba(11,18,32,0.62)]">{fmtDate(row.last_login_at)}</td>
              </tr>
            ))}
            {!isLoading && filtered.length === 0 ? (
              <tr><td colSpan="5" className="px-4 py-10 text-center text-sm text-[color:rgba(11,18,32,0.58)]">No student accounts match this search.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminStudents() {
  return (
    <div className="bg-[var(--ibp-offwhite)]">
      <section className="bg-[var(--ibp-navy-950)] py-12 text-white sm:py-14">
        <Container>
          <SectionHeading
            tone="navy"
            kicker="Store admin"
            title="Student accounts"
            subtitle="See who has a free account, who has full access, and whether purchase fulfillment reached the correct student."
          />
        </Container>
      </section>
      <section className="py-12 sm:py-16">
        <Container>
          <AdminGate><StudentManager /></AdminGate>
        </Container>
      </section>
    </div>
  );
}

